import { ISpeechService } from 'src/services/SpeechService/ISpeechService'

import { RUN_LENGTH, Scenes, TYPE_LINE_EVERY } from '../constants/game'

import { IExecutor } from './IExecutor'
import {
  DYNAMIC_COPY,
  KILL_RATE_REACTION,
  reactionBand,
} from 'src/constants/copy'
import { VictimService } from 'src/services/VictimService/VictimService'

import { AudioQueueManager } from 'src/services/AudioQueueManagerService/AudioQueueManagerService'
import { getOrdinalSuffix } from 'src/utils/getOrdinalSuffix'
import { getRandomCopyVariation } from 'src/utils/getRandomCopyVariation'
import { DefaultVictimOne } from 'src/constants/victim'
import { Victim } from 'src/types/victimTypes'
import { buildTypeLine } from 'src/utils/buildVerdict'

export type RunHud = {
  bodyCount: number
  runLength: number
  lovedOneStreak: number
}

export class DecisionStatsSceneExecutor extends IExecutor {
  private setScene: (scene: Scenes) => void
  private speech: ISpeechService
  private audioQueueManager: AudioQueueManager

  private victimService: VictimService

  constructor(
    setScene: (scene: Scenes) => void,
    speech: ISpeechService,
    audioQueueManager: AudioQueueManager,
    victimService: VictimService,
  ) {
    super()
    this.setScene = setScene
    this.speech = speech
    this.audioQueueManager = audioQueueManager
    this.victimService = victimService
  }

  public exec = async () => {
    console.log('🐄 DecisionStatsSceneExecutor exec')
  }

  public sayVictimStats = async () => {
    const numTimesKilledToOrdinal = getOrdinalSuffix(
      this.victimService.getNumTimesKilled(),
    )

    const murderVictimName =
      this.victimService.doomedVictim?.name ?? DefaultVictimOne.name
    const percent = this.victimService.getPercentKilled()
    const reaction = getRandomCopyVariation(
      KILL_RATE_REACTION[reactionBand(Number(percent))],
    )

    this.audioQueueManager.enqueue(() =>
      this.speech.speak(
        DYNAMIC_COPY.KILLED_NUMBER(murderVictimName, numTimesKilledToOrdinal),
      ),
    )

    // One line, so the number and the judgement land as a single beat.
    this.audioQueueManager.enqueue(() =>
      this.speech.speak(
        `${DYNAMIC_COPY.KILLED_PERCENT(murderVictimName, percent)} ${reaction}`,
      ),
    )

    const bodyCount = this.victimService.getBodyCount()
    const runOver = bodyCount >= RUN_LENGTH

    // Mid-run check-in ("Five down. You spare animals and murder your
    // loved ones. You have a type."). The verdict card covers the finale.
    if (!runOver && bodyCount > 0 && bodyCount % TYPE_LINE_EVERY === 0) {
      const typeLine =
        buildTypeLine(bodyCount, this.victimService.getTagTally()) ??
        DYNAMIC_COPY.NO_TYPE_LINE(bodyCount)
      this.audioQueueManager.enqueue(() => this.speech.speak(typeLine))
    }

    this.audioQueueManager.enqueue(async () => {
      this.setScene(runOver ? Scenes.Verdict : Scenes.WhoLivesQuestion)
    })
  }

  public getDoomedVictim = (): Victim => {
    return this.victimService.doomedVictim ?? (DefaultVictimOne as Victim)
  }

  public getRunHud = (): RunHud => ({
    bodyCount: this.victimService.getBodyCount(),
    runLength: RUN_LENGTH,
    lovedOneStreak: this.victimService.getRunTally().lovedOneStreak,
  })
}
